import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Tooltip,
  Select,
  DatePicker,
  message,
  Switch,
  Popconfirm,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Main from "../components/layout/Main";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import useDebounce from "../hooks/useDebounce";
import apiPath from "../constants/apiPath";
import moment from "moment";
import CURRENCY_OPTIONS from "../helper/currencyOption";

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;
const { RangePicker } = DatePicker;

function SubscriptionPriceManager() {
  const { request } = useRequest();
  const requestRef = useRef(request);

  // State management
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add"); // add, edit
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({
    sort: "newest",
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deletedFeatureIds, setDeletedFeatureIds] = useState([]);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const debouncedSearchText = useDebounce(searchText, 500);

  // Fetch subscriptions
  const fetchSubscriptions = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();

    // Add sort parameter
    if (filters.sort) {
      params.append("sort", filters.sort);
    }

    // Add search parameter
    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    // Add pagination parameters
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    requestRef.current({
      url: `/api/${apiPath.subscriptions}?${params.toString()}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);

        if (data?.status === 200 && data?.data) {
          // Handle the response structure based on your API specification
          setSubscriptions(Array.isArray(data.data) ? data.data : []);

          // Handle pagination from your API response
          if (data.pagination) {
            setPagination((prev) => ({
              ...prev,
              total: data.pagination.totalCount || 0,
              current: Number(data.pagination.currentPage) || prev.current,
              pageSize: data.pagination.limit || prev.pageSize,
            }));
          }
        } else {
          setSubscriptions([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          ShowToast(
            data?.message || "Failed to fetch subscriptions",
            Severty.ERROR
          );
        }
      },
      onError: (error) => {
        setLoading(false);
        setSubscriptions([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        ShowToast("Failed to fetch subscriptions", Severty.ERROR);
      },
    });
  }, [filters, debouncedSearchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Handlers
  const handleTableChange = (paginationInfo, filters, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleAddSubscription = () => {
    setModalType("add");
    setSelectedSubscription(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setModalType("edit");
    setModalVisible(true);
    setDeletedFeatureIds([]);

    const effectiveDates = [];
    if (subscription.effective_start) {
      effectiveDates.push(moment(subscription.effective_start));
    }
    if (subscription.effective_end) {
      effectiveDates.push(moment(subscription.effective_end));
    }

    form.setFieldsValue({
      name: subscription.name,
      duration: subscription.duration,
      base_price: subscription.base_price,
      discounted_price: subscription.discounted_price,
      currency: subscription.currency,
      status: subscription.status === "ACTIVE",
      effective_dates: effectiveDates.length > 0 ? effectiveDates : null,
      max_users: subscription.max_users ?? 0,
      features: subscription.features || [],
    });
  };

  // Create or update subscription
  const handleFormSubmit = async (values) => {
    try {
      const formattedFeatures = (values.features || []).map((f) => {
        // Existing feature
        if (f.id) {
          return {
            id: f.id,
            name: f.name,
            description: f.description,
          };
        }

        // New feature (NO id)
        return {
          name: f.name,
          description: f.description,
        };
      });
      const [startDate, endDate] = values.effective_dates || [];

      const subscriptionData = {
        name: values.name,
        duration: values.duration,
        base_price: parseFloat(values.base_price),
        discounted_price: values.discounted_price
          ? parseFloat(values.discounted_price)
          : null,
        currency: values.currency,
        effective_start: startDate ? startDate.format("YYYY-MM-DD") : null,
        effective_end: endDate ? endDate.format("YYYY-MM-DD") : null,
        status: values.status ? "ACTIVE" : "INACTIVE",
        max_users: values.max_users ? parseInt(values.max_users, 10) : 0,
        features: formattedFeatures,
      };

      if (modalType === "add") {
        setCreateLoading(true);
        requestRef.current({
          url: `/api/${apiPath.subscriptions}`,
          method: "POST",
          data: subscriptionData,
          onSuccess: (data) => {
            if (data?.status === 201) {
              ShowToast("Subscription created successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchSubscriptions();
            } else {
              ShowToast(
                data?.message || "Failed to create subscription",
                Severty.ERROR
              );
            }
            setCreateLoading(false);
          },
          onError: (error) => {
            console.log("its error",error?.response?.data?.message)
            ShowToast(
              error?.response?.data?.message || "Failed to create subscription",
              Severty.ERROR
            );
            setCreateLoading(false);
          },
        });
      } else {
        setUpdateLoading(true);
        requestRef.current({
          url: `/api/${apiPath.subscriptions}/${selectedSubscription.id}`,
          method: "PUT",
          data: {
            duration: values.duration,
            base_price: parseFloat(values.base_price),
            discounted_price: values.discounted_price
              ? parseFloat(values.discounted_price)
              : null,
            currency: values.currency,
            effective_start: startDate ? startDate.format("YYYY-MM-DD") : null,
            effective_end: endDate ? endDate.format("YYYY-MM-DD") : null,
            status: values.status ? "ACTIVE" : "INACTIVE",
            features: values.features || [],
            deleted_features: deletedFeatureIds,
          },
          onSuccess: (data) => {
            if (data?.status === 200) {
              ShowToast("Subscription updated successfully", Severty.SUCCESS);
              setModalVisible(false);
              fetchSubscriptions();
            } else {
              ShowToast(
                data?.message || "Failed to update subscription",
                Severty.ERROR
              );
            }
            setUpdateLoading(false);
          },
          onError: (error) => {
            ShowToast(
              error?.response?.data?.message || "Failed to update subscription",
              Severty.ERROR
            );
            setUpdateLoading(false);
          },
        });
      }
    } catch (error) {
      ShowToast("Failed to save subscription", Severty.ERROR);
      if (modalType === "add") {
        setCreateLoading(false);
      } else {
        setUpdateLoading(false);
      }
    }
  };

  // Delete subscription
  const handleDeleteSubscription = async (subscription) => {
    try {
      setDeleteLoading((prev) => ({ ...prev, [subscription.id]: true }));
      requestRef.current({
        url: `/api/${apiPath.subscriptions}/${subscription.id}`,
        method: "DELETE",
        onSuccess: (data) => {
          if (data?.status === 200) {
            ShowToast("Subscription deleted successfully", Severty.SUCCESS);
            fetchSubscriptions();
          } else {
            ShowToast(
              data?.message || "Failed to delete subscription",
              Severty.ERROR
            );
          }
          setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
        },
        onError: (error) => {
          ShowToast(
            error?.message || "Failed to delete subscription",
            Severty.ERROR
          );
          setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
        },
      });
    } catch (error) {
      ShowToast("Failed to delete subscription", Severty.ERROR);
      setDeleteLoading((prev) => ({ ...prev, [subscription.id]: false }));
    }
  };

  // Toggle subscription status
  const toggleSubscriptionStatus = async (subscription, newStatus) => {
    try {
      requestRef.current({
        url: `/api/${apiPath.subscriptions}/${subscription.id}`,
        method: "PUT",
        data: {
          status: newStatus,
        },
        onSuccess: (data) => {
          if (data?.status === 200) {
            ShowToast(
              `Subscription ${
                newStatus === "ACTIVE" ? "activated" : "deactivated"
              } successfully`,
              Severty.SUCCESS
            );
            fetchSubscriptions();
          } else {
            ShowToast(
              data?.message || "Failed to update subscription status",
              Severty.ERROR
            );
          }
        },
        onError: (error) => {
          ShowToast("Failed to update subscription status", Severty.ERROR);
        },
      });
    } catch (error) {
      ShowToast("Failed to update subscription status", Severty.ERROR);
    }
  };

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (id) => <Text code>PACKAGE_{id}</Text>,
    },
    {
      title: "Package Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (duration) => <span>{duration}</span>,
    },
    {
      title: "Base Price",
      dataIndex: "base_price",
      key: "base_price",
      render: (price, record) => (
        <span>
          {record.currency} {price}
        </span>
      ),
    },
    {
      title: "Discounted Price",
      dataIndex: "discounted_price",
      key: "discounted_price",
      render: (price, record) => (
        <span>{price ? `${record.currency} ${price}` : "-"}</span>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Tag color={status === "ACTIVE" ? "green" : "red"}>{status}</Tag>
      ),
    },
    {
      title: "Created on",
      dataIndex: "created_at",
      key: "created_at",
      width: 200,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Last Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 200,
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditSubscription(record)}
            />
          </Tooltip>
          <Tooltip
            title={record.status === "ACTIVE" ? "Deactivate" : "Activate"}
          >
            <Button
              type={record.status === "ACTIVE" ? "default" : "primary"}
              onClick={() =>
                toggleSubscriptionStatus(
                  record,
                  record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
                )
              }
            >
              {record.status === "ACTIVE" ? "Deactivate" : "Activate"}
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete Subscription"
            description="Are you sure you want to delete this subscription?"
            onConfirm={() => handleDeleteSubscription(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<DeleteOutlined />}
              danger
              loading={deleteLoading[record.id]}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <>
            <span>Subscription Price Manager</span>
            <p>Manage pricing for all predefined subscription packages</p>
          </>
        }
        extra={
          <Space wrap>
            <Search
              placeholder="Search subscriptions"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Sort By"
              style={{ width: 120 }}
              value={filters.sort}
              onChange={(value) => handleFilterChange("sort", value)}
            >
              <Option value="newest">Newest</Option>
              <Option value="oldest">Oldest</Option>
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddSubscription}
            >
              Add Subscription
            </Button>
          </Space>
        }
      >
        {/* Subscriptions Table */}
        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={subscriptions}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `Showing ${range[0]}-${range[1]} of ${total} subscriptions`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>

      {/* Edit Subscription Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {modalType === "add"
              ? "Add Subscription Package"
              : "Edit Subscription Package"}
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setDeletedFeatureIds([]);
          setModalVisible(false);
        }}
        footer={null}
        width={600}
        confirmLoading={createLoading || updateLoading}
      >
        {modalType === "edit" && selectedSubscription && (
          <div style={{ marginBottom: 16 }}>
            <h3>{selectedSubscription.name}</h3>
            <p>Duration: {selectedSubscription.duration}</p>
          </div>
        )}
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          {modalType === "add" && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="Package Name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter package name",
                      },
                      {
                        pattern: /^[A-Za-z0-9 _-]+$/,
                        message: "Special characters are not allowed",
                      },
                    ]}
                  >
                    <Input placeholder="Enter package name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="duration"
                    label="Duration"
                    rules={[
                      {
                        required: true,
                        message: "Please select duration",
                      },
                    ]}
                  >
                    <Select placeholder="Select duration">
                      <Option value="MONTHLY">Monthly</Option>
                      <Option value="QUARTERLY">Quarterly</Option>
                      <Option value="YEARLY">Yearly</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                    rules={[
                      {
                        required: true,
                        message: "Please enter currency",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select currency"
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase()) ||
                        option.value.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {CURRENCY_OPTIONS.map((currency) => (
                        <Option key={currency.value} value={currency.value} label={currency.label}>
                          {currency.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="base_price"
                label="Base Price"
                rules={[
                  {
                    required: true,
                    message: "Please enter base price",
                  },
                  {
                    pattern: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a valid price",
                  },
                ]}
              >
                <Input
                  placeholder="Enter base price"
                  type="number"
                  step="0.01"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discounted_price"
                label="Discounted Price (Optional)"
                rules={[
                  {
                    pattern: /^\d+(\.\d{1,2})?$/,
                    message: "Please enter a valid price",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const basePrice = getFieldValue("base_price");

                      // If discounted price is empty, it's valid
                      if (!value || !basePrice) {
                        return Promise.resolve();
                      }

                      if (parseFloat(value) > parseFloat(basePrice)) {
                        return Promise.reject(
                          new Error("Discounted price cannot be greater than base price")
                        );
                      }

                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  placeholder="Enter discounted price"
                  type="number"
                  step="0.01"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="effective_dates"
            label="Effective Date Range (Optional)"
          >
            <RangePicker style={{ width: "100%" }} disabledDate={(current) => current && current < moment().startOf("day")} />
          </Form.Item>

          <Form.Item
            name="max_users"
            label="Max Users"
            rules={[
              {
                pattern: /^\d+$/,
                message: "Please enter a valid whole number",
              },
            ]}
          >
            <Input
              placeholder="Enter max users (0 = unlimited)"
              type="number"
              min={0}
            />
          </Form.Item>

          <Form.List name="features"
            rules={[
              {
                validator: async (_, features) => {
                  if (!features || features.length < 2) {
                    return Promise.reject(
                      new Error("At least two features are required")
                    );
                  }
                },
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                <Row justify="space-between" align="middle">
                  <Col>
                    <h3 style={{ marginBottom: 0 }}>Features</h3>

                    {/* 👇 Helper / validation text */}
                    <span
                      style={{
                        fontSize: 12,
                        color: errors.length ? "#ff4d4f" : "#8c8c8c",
                      }}
                    >
                      Minimum two features required
                    </span>
                  </Col>

                  <Col>
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add()}
                    >
                      Add Feature
                    </Button>
                  </Col>
                </Row>

                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginTop: 12 }}
                    title={`Feature ${name + 1}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          const features = form.getFieldValue("features");
                          const featureToDelete = features?.[name];

                          if (modalType === "edit" && featureToDelete?.id) {
                            setDeletedFeatureIds((prev) => [...prev, featureToDelete.id]);
                          }

                          remove(name);
                        }}
                      />
                    }
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          label="Feature Name"
                          rules={[
                            { required: true, message: "Enter feature name" },
                          ]}
                        >
                          <Input placeholder="Feature name" />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "description"]}
                          label="Feature Description"
                          rules={[
                            { required: true, message: "Enter feature description" },
                          ]}
                        >
                          <Input placeholder="Feature description" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </>
            )}
          </Form.List>
          
          <Form.Item
            name="status"
            label="Status"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item shouldUpdate style={{ marginBottom: 0, textAlign: "right" }}>
            {() => {
              const basePrice = form.getFieldValue("base_price");
              const discountedPrice = form.getFieldValue("discounted_price");

              const isInvalidDiscount =
                basePrice &&
                discountedPrice &&
                parseFloat(discountedPrice) > parseFloat(basePrice);

              const hasErrors = form
                .getFieldsError()
                .some(({ errors }) => errors.length > 0);

              return (
                <>
                  <Button
                    style={{ marginRight: 8 }}
                    onClick={() => setModalVisible(false)}
                    disabled={createLoading || updateLoading}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={modalType === "add" ? createLoading : updateLoading}
                    disabled={
                      createLoading ||
                      updateLoading ||
                      isInvalidDiscount ||
                      hasErrors
                    }
                  >
                    {modalType === "add" ? "Create" : "Update"}
                  </Button>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default SubscriptionPriceManager;
